import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";

export default function BuyerDashboard() {
  const [escrows, setEscrows] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("Vehicle does not match report");
  const [details, setDetails] = useState("");

  const load = async () => {
    try {
      const [e, d] = await Promise.all([api.get("/escrow/mine"), api.get("/disputes")]);
      setEscrows(e.data); setDisputes(d.data);
    } catch { }
  };
  useEffect(() => { load(); }, []);

  const openDispute = async () => {
    try {
      await api.post("/disputes", { escrow_id: active.id, reason, details });
      toast.success("Dispute filed — admin will review");
      setActive(null); setDetails(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="kicker">Buyer Portal</div>
          <h1 className="display text-4xl">Your escrow deposits</h1>
        </div>

        <div className="border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-secondary/60"><tr>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Vehicle</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Amount</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Reference</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Actions</th>
            </tr></thead>
            <tbody>
              {escrows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground text-sm">You haven't placed any escrow deposits yet. <Link to="/marketplace" className="text-primary hover:underline">Browse verified cars →</Link></td></tr>}
              {escrows.map((e) => (
                <tr key={e.id} className="border-t border-border" data-testid={`buyer-escrow-${e.id}`}>
                  <td className="py-3 px-3">
                    <Link to={`/vehicles/${e.vehicle_id}`} className="hover:text-primary">
                      {e.vehicle ? `${e.vehicle.year} ${e.vehicle.make} ${e.vehicle.model}` : e.vehicle_id}
                    </Link>
                  </td>
                  <td className="py-3 px-3 mono">${Number(e.amount_usd).toLocaleString()}</td>
                  <td className="py-3 px-3"><span className="mono text-[10px] px-2 py-1 bg-secondary rounded-sm uppercase">{e.status}</span></td>
                  <td className="py-3 px-3 mono text-xs">{e.payment_reference || "—"}</td>
                  <td className="py-3 px-3">
                    {["committed", "verified", "inspection_pending"].includes(e.status) && (
                      <Button variant="outline" size="sm" onClick={() => setActive(e)} data-testid={`open-dispute-${e.id}`}>
                        <AlertTriangle className="h-4 w-4 mr-1" /> File Dispute
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10">
          <h2 className="display text-2xl mb-3">Your disputes</h2>
          <div className="border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary/60"><tr>
                <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Reason</th>
                <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Status</th>
                <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Details</th>
              </tr></thead>
              <tbody>
                {disputes.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-muted-foreground text-sm">No disputes.</td></tr>}
                {disputes.map(d => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="py-3 px-3">{d.reason}</td>
                    <td className="py-3 px-3"><span className="mono text-[10px] px-2 py-1 bg-secondary rounded-sm uppercase">{d.status}</span></td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">{d.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>File a dispute</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="kicker">Reason (short)</label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dispute-reason-input" />
              </div>
              <div>
                <label className="kicker">Details</label>
                <Textarea rows={5} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="dispute-details-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={openDispute} className="bg-accent hover:bg-accent/90" data-testid="dispute-submit-btn">Submit dispute</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
}
